/**
 * "Release device" — the customer takes the device home while the job waits
 * for parts or a supplier quote. Shared by the ticket page and the mobile job
 * view. Who and when are stamped server-side; this asks only what the job is
 * waiting on and an optional note.
 */

import React, { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';

import type { WorkItem } from '../workItemTypes';
import { releaseWorkItemDevice } from '../../../services/workItemDataService';

export interface ReleaseDeviceDialogProps {
  open: boolean;
  workItem: WorkItem;
  onClose: () => void;
  /** Called with the fields to patch onto the work item after a release. */
  onReleased: (updates: Partial<WorkItem>) => void;
}

export const ReleaseDeviceDialog: React.FC<ReleaseDeviceDialogProps> = ({
  open,
  workItem,
  onClose,
  onReleased,
}) => {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRelease = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await releaseWorkItemDevice(workItem.guid, {
        reason: reason.trim() || undefined,
        note: note.trim() || undefined,
      });
      onReleased({
        deviceWithCustomer: true,
        deviceReleasedAt: result.deviceReleasedAt,
        deviceReleasedBy: result.deviceReleasedBy,
        deviceReleaseReason: result.deviceReleaseReason ?? undefined,
        deviceReleaseNote: result.deviceReleaseNote ?? undefined,
      });
      setReason('');
      setNote('');
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to release device');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} fullWidth maxWidth="xs">
      <DialogTitle>Release device to customer</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          The job stays open with its status. When the customer brings the device
          back, start a Drop Off check-in against this job — scanning its label
          does that — and the device reads as back in the shop.
        </DialogContentText>
        <Stack spacing={2}>
          <TextField
            label="Waiting on"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Battery, ETA 2 weeks"
            fullWidth
            disabled={busy}
            inputProps={{ maxLength: 500 }}
          />
          <TextField
            label="Note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            disabled={busy}
            inputProps={{ maxLength: 4000 }}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={handleRelease} disabled={busy}>
          {busy ? 'Releasing…' : 'Release device'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
